import React from 'react';
import { Question, OASISFormData, FormValue } from './types';
import { RadioQuestion } from './questions/RadioQuestion';
import { TextQuestion } from './questions/TextQuestion';
import { DateQuestion } from './questions/DateQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { NumberQuestion } from './questions/NumberQuestion';
import {
  GridSpan,
  resolveQuestionSpan,
  getSubgroupChildSpanClass,
} from './OASISFormUtils';

interface OASISQuestionRendererProps {
  question: Question;
  value: FormValue;
  onChange: (value: FormValue) => void;
  formData: OASISFormData;
  onFormDataChange?: (updates: Partial<OASISFormData>) => void;
  spanOverrides?: Record<string, GridSpan>;
  containerSpan?: GridSpan;
  highlightedQid?: string | null;
}

export const OASISQuestionRenderer: React.FC<OASISQuestionRendererProps> = ({
  question,
  value,
  onChange,
  formData,
  onFormDataChange,
  spanOverrides,
  containerSpan = 12,
  highlightedQid,
}) => {
  const isHighlighted = highlightedQid === question.qid;
  const isPseudo = question.is_pseudo === true;
  const hasSelectedValue = (candidate: FormValue): boolean => {
    if (candidate === undefined || candidate === null || candidate === '') {
      return false;
    }
    if (Array.isArray(candidate) && candidate.length === 0) {
      return false;
    }
    return true;
  };

  const getActiveSubitems = (subitemValue: FormValue): Question[] => {
    if (!question.subitems) return [];

    const selectedValues =
      typeof subitemValue === 'string'
        ? [subitemValue]
        : Array.isArray(subitemValue)
          ? subitemValue.filter(
              (value): value is string => typeof value === 'string'
            )
          : [];

    const seenQids = new Set<string>();
    const activeSubitems: Question[] = [];

    selectedValues.forEach(selectedValue => {
      (question.subitems?.[selectedValue] ?? []).forEach(subitem => {
        if (seenQids.has(subitem.qid)) return;
        seenQids.add(subitem.qid);
        activeSubitems.push(subitem);
      });
    });

    return activeSubitems;
  };

  // For pure pseudo questions with "Yes" subitems, auto-select "Yes" and render subitems directly
  const effectiveValue = isPseudo && question.subitems?.['Yes'] ? 'Yes' : value;

  // Render subitems if they exist and the parent has a matching value
  const renderSubitems = (subitemValue: FormValue) => {
    if (!question.subitems || !hasSelectedValue(subitemValue)) return null;

    const subitems = getActiveSubitems(subitemValue);
    if (subitems.length === 0) return null;

    const subitemsGridClass = 'grid grid-cols-12 gap-3';
    const nestedWrapperClass = 'ml-3 mt-3 border-l-2 border-primary/20 pl-3';
    const subitemsGrid = (
      <div className={subitemsGridClass}>
        {subitems.map((subitem: Question) => {
          const subitemSpan = resolveQuestionSpan(subitem, spanOverrides);
          const effectiveSubitemContainerSpan = Math.min(
            subitemSpan,
            containerSpan
          ) as GridSpan;
          return (
            <div
              key={subitem.qid}
              className={getSubgroupChildSpanClass(subitemSpan, containerSpan)}
            >
              <OASISQuestionRenderer
                question={subitem}
                value={formData[subitem.qid]}
                onChange={(subValue: FormValue) => {
                  if (onFormDataChange) {
                    const updates: Partial<OASISFormData> = {
                      [subitem.qid]: subValue,
                    };
                    // If parent is a pseudo question with "Yes" option, set it to "Yes" when subitem changes
                    if (isPseudo && question.subitems?.['Yes']) {
                      updates[question.qid] = 'Yes';
                    }
                    onFormDataChange(updates);
                  } else {
                    onChange(subValue);
                  }
                }}
                formData={formData}
                onFormDataChange={onFormDataChange}
                spanOverrides={spanOverrides}
                containerSpan={effectiveSubitemContainerSpan}
              />
            </div>
          );
        })}
      </div>
    );

    if (isPseudo) return subitemsGrid;
    return <div className={nestedWrapperClass}>{subitemsGrid}</div>;
  };

  const renderQuestion = () => {
    switch (question.type) {
      case 'radio':
        return (
          <RadioQuestion
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
          />
        );

      case 'text':
        return (
          <TextQuestion
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
          />
        );

      case 'number':
        return (
          <NumberQuestion
            question={question}
            value={
              typeof value === 'number'
                ? value
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={onChange}
          />
        );

      case 'date':
        return (
          <DateQuestion
            question={question}
            value={
              value instanceof Date
                ? value
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={onChange}
          />
        );

      case 'multiselect':
        return (
          <MultiSelectQuestion
            question={question}
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
          />
        );

      default:
        return (
          <div className='text-destructive text-xs'>
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  // For pseudo questions with "Yes" subitems:
  // If flattened at the grid level (no subitems wrapper needed), just render the question itself.
  // If called standalone (e.g. as a nested subitem), still render subitems directly.
  if (isPseudo && question.subitems?.['Yes']) {
    return <>{renderSubitems('Yes')}</>;
  }

  // Show a hint when the question has conditional subitems but hasn't been answered yet.
  const hasRevealableSubitems =
    !isPseudo &&
    question.subitems &&
    Object.values(question.subitems).some(arr => arr.length > 0);

  return (
    <div
      id={`oasis-q-${question.qid}`}
      className={`rounded-lg border px-3.5 py-3 overflow-hidden h-full transition-all duration-500 ${
        isHighlighted
          ? 'border-destructive/50 bg-destructive/5 ring-1 ring-destructive/30'
          : 'border-border/40 bg-muted/30'
      }`}
    >
      {renderQuestion()}
      <span className='text-[9px] font-mono text-muted-foreground/50 select-all'>
        {question.oasis_ids?.length ? `${question.oasis_ids.join(', ')}` : ''}
      </span>
      {hasRevealableSubitems && !hasSelectedValue(effectiveValue) && (
        <p className='text-[11px] text-muted-foreground/70 mt-1.5 italic'>
          Answering may reveal more items
        </p>
      )}
      {renderSubitems(effectiveValue)}
    </div>
  );
};
